import * as React from "react";
import Link from "next/link";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Image from "next/image";
import {
  CloseRounded,
  HomeRounded,
  InfoRounded,
  EventRounded,
  ArticleRounded,
  MenuBookRounded,
  BuildRounded,
  PhotoCameraRounded,
  VideoLibraryRounded,
  ContactMailRounded,
  FavoriteRounded,
  VolunteerActivismRounded,
} from "@mui/icons-material";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { Button, Divider } from "@mui/material";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function HeaderMobile({ open, setOpen }: Props) {
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const { t, i18n } = useTranslation("common");
  const router = useRouter();
  const lang = i18n.language;
  const isAr = lang === "ar";

  const toggleLanguageHandler = (lang: string) => {
    i18n.changeLanguage(lang);
    router.push(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { locale: lang },
    );
  };

  const DrawerList = (
    <Box
      sx={{ width: "80vw" }}
      role="presentation"
      className="flex flex-col gap-[0.5rem] mt-[2rem]"
    >
      <Link href="/">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/" ? "bg-gray-200" : ""
          }`}
        >
          <HomeRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.home")}
          </span>
        </div>
      </Link>
      <Link href="/about">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/about" ? "bg-gray-200" : ""
          }`}
        >
          <InfoRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.about")}
          </span>
        </div>
      </Link>
      <Link href="/events">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/events" ? "bg-gray-200" : ""
          }`}
        >
          <EventRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.events")}
          </span>
        </div>
      </Link>
      <Link href="/news">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/news" ? "bg-gray-200" : ""
          }`}
        >
          <ArticleRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.news")}
          </span>
        </div>
      </Link>
      {/* <Link href="/blogs">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/blogs" ||
            router.pathname.startsWith("/blogs/")
              ? "bg-gray-200"
              : ""
          }`}
        >
          <MenuBookRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">{t("blogs.title")}</span>
        </div>
      </Link> */}
      <Link href="/services">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/services" ||
            router.pathname.startsWith("/services/")
              ? "bg-gray-200"
              : ""
          }`}
        >
          <BuildRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.services")}
          </span>
        </div>
      </Link>

      <Link href="/contact">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/contact" ? "bg-gray-200" : ""
          }`}
        >
          <ContactMailRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.contact")}
          </span>
        </div>
      </Link>
      <Link href="/donate">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/donate" ? "bg-gray-200" : ""
          }`}
        >
          <FavoriteRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.donate")}
          </span>
        </div>
      </Link>
      <Link href="/volunteer">
        <div
          onClick={toggleDrawer(false)}
          className={`flex items-center gap-[1rem] hover:bg-gray-200 py-[0.7rem] px-[2rem] ${
            router.pathname === "/volunteer" ? "bg-gray-200" : ""
          }`}
        >
          <VolunteerActivismRounded className="text-secondary" />
          <span className="text-black text-[1.2rem]">
            {t("header.nav.volunteer")}
          </span>
        </div>
      </Link>
    </Box>
  );

  return (
    <div>
      <Drawer
        style={{ direction: isAr ? "rtl" : "ltr" }}
        anchor={isAr ? "right" : "left"}
        open={open}
        onClose={toggleDrawer(false)}
        className="child:no-scrollbar "
      >
        <div className="relative pt-[1rem] h-[100vh] flex flex-col bg-white">
          <Image
            src={"/icons/logo.png"}
            alt="logo"
            width={1000}
            height={1000}
            className="w-[10rem]  mx-auto"
          />
          <Divider className="my-2 border-[0.15rem] border-primary" />
          <CloseRounded
            sx={{ stroke: "white", strokeWidth: 0.5 }}
            onClick={toggleDrawer(false)}
            className={`absolute text-black text-[2.1rem] ${
              isAr ? "left-6" : "right-6"
            }  top-6`}
          />
          <div className="flex-1 overflow-y-auto">{DrawerList}</div>

          <div className=" mt-auto flex items-center justify-center flex-col gap-[3rem] mb-4 text-primary ">
            <div
              style={{ direction: "ltr" }}
              className="w-full flex items-center justify-center mt-[3rem]"
            >
              <div className="grid grid-cols-2 w-[20rem] child:h-[3.5rem] gap-x-[0.7rem] gap-y-[1.7rem] text-white text-[1.2rem] font-bold">
                <Button
                  onClick={() => toggleLanguageHandler("ar")}
                  variant="contained"
                  className="rounded-full bg-secondary"
                >
                  عربي
                </Button>
                <Button
                  onClick={() => toggleLanguageHandler("en")}
                  variant="contained"
                  className="rounded-full bg-secondary"
                >
                  English
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
